import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MessService } from '../mess.service';
import { NgxSpinnerService } from 'ngx-spinner';

/** Head-count summary (same aggregates as before). */
export interface MealOrderHeadcountSummary {
  studentCount: number;
  employeeCount: number;
  subStaffCount: number;
  visitorsCount: number;
}

/** One row in the meal breakdown table (Students / Staff / Sub-Staff / Others / Total). */
export interface MealOrderMatrixRow {
  label: string;
  heads: number;
  breakfast: number;
  lunch: number;
  snacks: number;
  dinner: number;
}

export interface MealOrderCampusDetail {
  campusName: string;
  dateLabel: string;
  headcounts: MealOrderHeadcountSummary;
  /** Meal type → total heads for that campus (unchanged for callers). */
  mealHeads: { mealType: string; heads: number }[];
  matrix: MealOrderMatrixRow[];
  matrixTotals: MealOrderMatrixRow;
  /** Optional; when set, Call Now uses `tel:` link. */
  callPhone?: string;
  /** Optional; from API when provided. */
  requestedBy?: string;
  /** Optional; from API when provided. */
  requestedMobileNo?: string;
  /** Optional; from API when provided. */
  requestedUsername?: string;
  /** Optional; from API when provided — used for profile image lookup. */
  requestedPayrollId?: string;
}

@Component({
  selector: 'app-meal-order-dialog',
  templateUrl: './meal-order-dialog.component.html',
  styleUrls: ['./meal-order-dialog.component.scss'],
})
export class MealOrderDialogComponent implements OnInit, OnDestroy {
  @Input() detail: MealOrderCampusDetail | null = null;

  @Output() readonly closed = new EventEmitter<void>();

  userId = '';
  userName = '';
  requestedPayrollId = '';
  profileImageObjectUrl: string | null = null;

  private profileImageRevokeUrl: string | null = null;

  constructor(private messService: MessService, private spinner: NgxSpinnerService) {}

  ngOnInit(): void {
    // Row click sends requestedUsername / requestedPayrollId — prefer them over localStorage.
    this.requestedPayrollId = String(this.detail?.requestedPayrollId ?? '').trim();
    this.userId = this.requestedPayrollId || (localStorage.getItem('userId') ?? '');
    this.userName =
      String(this.detail?.requestedUsername ?? '').trim() ||
      (localStorage.getItem('userName') ?? '');
    if (this.userId) {
      this.loadProfileImage();
    }
  }

  ngOnDestroy(): void {
    this.revokeProfileImageObjectUrl();
  }

  private revokeProfileImageObjectUrl(): void {
    if (this.profileImageRevokeUrl) {
      try {
        URL.revokeObjectURL(this.profileImageRevokeUrl);
      } catch {
        /* ignore */
      }
      this.profileImageRevokeUrl = null;
    }
    this.profileImageObjectUrl = null;
  }

  private loadProfileImage(): void {
    this.revokeProfileImageObjectUrl();
  this.spinner.show();
    this.messService.employeeProfileImage(this.requestedPayrollId || this.userId).subscribe({

      next: (blob) => {
        if (!(blob instanceof Blob) || !blob.size) {
          this.spinner.hide();
          return;
        }
        if (blob.type && !blob.type.startsWith('image/')) {
          this.spinner.hide();
          return;
        }
        const url = URL.createObjectURL(blob);
        this.profileImageRevokeUrl = url;
        this.profileImageObjectUrl = url;
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
      },
    });
  }

  close(): void {
    this.closed.emit();
  }

  onCallNow(): void {
    const raw = String(
      this.detail?.callPhone ?? this.detail?.requestedMobileNo ?? '',
    ).trim();
    if (!raw) {
      return;
    }
    const digits = raw.replace(/[^\d+]/g, '');
    if (!digits) {
      return;
    }
    window.location.href = `tel:${digits}`;
  }

  formatNum(value: number | null | undefined): string {
    const n = Number(value ?? 0);
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(n);
  }

  /** Average = Total / number of meals that have a count (e.g. 4 meals → /4, Breakfast+Lunch → /2). */
  formatAverage(row: MealOrderMatrixRow | null | undefined): string {
    if (!row) {
      return this.formatNum(0);
    }
    const enteredMeals = [row.breakfast, row.lunch, row.snacks, row.dinner]
      .map((v) => Number(v ?? 0))
      .filter((v) => v > 0).length;
    if (enteredMeals <= 0) {
      return this.formatNum(0);
    }
    return this.formatNum(Math.round(Number(row.heads ?? 0) / enteredMeals));
  }

  totalHeads(h: MealOrderHeadcountSummary | null | undefined): number {
    if (!h) {
      return 0;
    }
    return (
      Number(h.studentCount ?? 0) +
      Number(h.employeeCount ?? 0) +
      Number(h.subStaffCount ?? 0) +
      Number(h.visitorsCount ?? 0)
    );
  }
}
